import * as React from "react";
import { toast } from "react-toastify";

import { Table } from "src/library/components/Tables";
import { ITableInstance } from "src/library/components/Tables/Table";
import { LikeToDo } from "src/library/components/LikeToDo";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/core/shared/api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  IUserGroup,
  useUserApi
} from "src/core/users/api/users.api";
import { userGroupTableColumns } from "src/core/shared/components/tables";

// User groups page
const UserGroupsPage: React.ReactFCWithChildren<PageProps<unknown>> = ({ title, ...props }) => {
  const TableRef = React.useRef<ITableInstance<IUserGroup>>(null);
  const [ isLoading, setIsLoading ] = React.useState(false);
  const [ userGroupsData, setUserGroupsData ] = React.useState<PaginatedData<IUserGroup>>();
  const [ listUserGroupsRes, listUserGroupsReq ] = useUserApi("LIST_USER_GROUPS");
  const [ deleteUserGroupRes, deleteUserGroupReq ] = useUserApi("DELETE_USER_GROUP");
  const checkPermission = useCheckUserPermission();
  const canAmendUserGroup = checkPermission("amend-user");
  const canDeleteUserGroup = checkPermission("delete-user");
  const canViewUserGroup = checkPermission("view-user");
  const canCreateUserGroup = checkPermission("create-user");

  // Table actions
  const tableActions = React.useCallback((row: any) => {
    const actions = [];

    if (canAmendUserGroup) {
      actions.push({
        title: "Edit user group",
        action: () => props.history.push(`/admin/users/groups/edit/${row.original.id}`)
      });
    }

    if (canDeleteUserGroup) {
      actions.push({
        title: "Delete user group",
        action: () => deleteUserGroupReq({ id: row.original.id })
      });
    }

    if (!actions.length && canViewUserGroup) {
      actions.push({
        title: "View",
        action: () => props.history.push(`/admin/users/groups/edit/${row.original.id}`)
      });
    }

    return actions;
  }, [
    canAmendUserGroup,
    canDeleteUserGroup,
    canViewUserGroup,
    deleteUserGroupReq,
    props.history
  ]);

  // Like to do buttons
  const LikeToDoButtons = React.useMemo((): JSX.Element[] => {
    const buttons = [
      <Button
        key="create-user-group"
        onClick={() => props.history.push("/admin/users/groups/create")}
      >
        Create new user group
      </Button>
    ];

    return buttons;
  }, [ props.history ]);

  // Fetches the table data
  const onFetchData = React.useCallback((filters: FilterParams<IUserGroup>, pagination: PaginatedParams<IUserGroup>) => {
    setIsLoading(true);

    listUserGroupsReq({
      filters,
      pagination
    });
  }, [ listUserGroupsReq ]);

  // On listUserGroupsRes change
  React.useEffect(() => {
    if (listUserGroupsRes.error) {
      setIsLoading(false);
      toast.error(listUserGroupsRes.error.message);
    }

    if (listUserGroupsRes.data) {
      setIsLoading(false);
      setUserGroupsData(listUserGroupsRes.data);
    }
  }, [ listUserGroupsRes.error, listUserGroupsRes.data ]);

  // On deleteUserGroupRes change
  React.useEffect(() => {
    if (deleteUserGroupRes.error) {
      setIsLoading(false);
      toast.error(deleteUserGroupRes.error.message);
    }

    if (deleteUserGroupRes.data) {
      setIsLoading(false);

      if (TableRef.current) {
        TableRef.current.reloadTable();
      }
    }
  }, [ deleteUserGroupRes.error, deleteUserGroupRes.data ]);

  return (
    <Layout
      title={title}
      {...props}
    >
      <MainContentWrapper
        fill
        Header={canCreateUserGroup && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={LikeToDoButtons}
          />
        )}
      >
        <Table
          tableId="user-groups"
          ref={TableRef}
          loading={isLoading}
          tableOf="UserGroups"
          viewAllowedToToggle
          data={userGroupsData ? userGroupsData.result : []}
          onFetchData={onFetchData}
          pages={userGroupsData ? userGroupsData.pages : 0}
          totalRows={userGroupsData ? userGroupsData.count : 0}
          columns={userGroupTableColumns({
            ActionButton: row => {
              const actions = tableActions(row);

              return actions.length ? (
                <ActionButton
                  title="actions"
                  items={tableActions(row)}
                />
              ) : null;
            }
          })}
        />
      </MainContentWrapper>
    </Layout>
  );
};

export default UserGroupsPage;
